import React, { useState } from 'react';
import { Collapse, Row, Table } from 'reactstrap';
import { KaEmployeeProfile } from '../UserAccess/UserContext';
import { Button } from 'react-bootstrap';
import BoldButton from '../Common/BoldButton';
import { ToggleableItems } from '../Common/useToggleableItems';
import { BusinessDto, SiteDto } from '../../requests/employee/useGetBusinesses';
import FilterInput, { textSatisfiesFilter } from '../FilterInput';

interface BusinessListProps {
  user: KaEmployeeProfile;
  businesses: BusinessDto[];
  expanded: ToggleableItems;
  addUserClicked: (business: BusinessDto) => void;
  importProducts: (business: BusinessDto) => void;
  importDrivers: (business: BusinessDto) => void;
  importCustomers: (business: BusinessDto) => void;
  importCarriers: (business: BusinessDto) => void;
  manageSites: (business: BusinessDto) => void;
  createSite: (business: BusinessDto) => void;
  editSite: (site: SiteDto) => void;
  disableSites: (business: BusinessDto) => void;
  enableSites: (business: BusinessDto) => void;
  createBusiness: () => void;
}

const BusinessList = (props: BusinessListProps) => {
  const { expanded } = props;
  const [filter, setFilter] = useState('');

  const matchesFilter = (b: BusinessDto) => {
    const satisfiesFilter = textSatisfiesFilter(filter);
    return satisfiesFilter(b.name) ||
      satisfiesFilter(b.serviceContractNumber) ||
      b.sites.some(s => satisfiesFilter(s.customerName)
        || satisfiesFilter(s.internalName)
        || satisfiesFilter(s.internalSiteId));
  };

  const businesses = props.businesses
    .filter(matchesFilter)
    .sort((a, b) => Number(a.sandbox) - Number(b.sandbox) || a.name.localeCompare(b.name));

  return (<>
    <Row className="justify-content-between align-items-center mb-2">
      <h2 className="col-auto ka-blue">Businesses</h2>
      <BoldButton className="col-auto btn-ghost-primary"
        disabled={!props.user.canEditBusinesses}
        onClick={props.createBusiness}>
        Create Business
      </BoldButton>
    </Row>
    <div className="row mb-3">
      <div className="col-4">
        <FilterInput 
          placeholder="Search businesses..."
          value={filter}
          onFilterTextChange={setFilter}
        />
      </div>
    </div>
    <Table borderless className="align-middle">
      <thead>
        <tr className="border-bottom border-dark border-1">
          <th className="col-6">Name</th>
          <th className="col-2">Service Contract</th>
          <th className="col-1 text-center">Total Sites</th>
          <th className="col-1 text-center">Unused Sites</th>
          <th className="col-2" />
        </tr>
      </thead>
      <tbody>
        {businesses.map(business =>
          <React.Fragment key={business.id}>
            <tr className="border-top">
              <td className="preserve-whitespace">
                {business.name}{business.disabled && ' (Disabled)'}{business.sandbox && ' (Sandbox)'}
              </td>
              <td>{business.serviceContractNumber}</td>
              <td className="text-center">{business.siteAllowance}</td>
              <td className="text-center">{business.siteAllowance - business.sites.length}</td>
              <td>
                <div className="d-flex justify-content-end align-items-center gap-1">
                  <div className="dropdown">
                    <Button className='dropdown-toggle btn-ghost-secondary' variant='link' data-bs-toggle='dropdown' aria-expanded='false'>
                      <strong>Actions</strong>
                    </Button>
                    <div className='dropdown-menu'>
                      <BoldButton className='dropdown-item btn-ghost-secondary'
                        disabled={!props.user.canEditBusinesses}
                        onClick={() => props.addUserClicked(business)}>
                        Add user
                      </BoldButton>
                      <BoldButton className='dropdown-item btn-ghost-secondary'
                        disabled={!props.user.canEditBusinesses}
                        onClick={() => props.importProducts(business)}>
                        Import products
                      </BoldButton>
                      <BoldButton className='dropdown-item btn-ghost-secondary'
                        disabled={!props.user.canEditBusinesses}
                        onClick={() => props.importDrivers(business)}>
                        Import drivers
                      </BoldButton>
                      <BoldButton className='dropdown-item btn-ghost-secondary'
                        disabled={!props.user.canEditBusinesses}
                        onClick={() => props.importCustomers(business)}>
                        Import customers
                      </BoldButton>
                      <BoldButton className='dropdown-item btn-ghost-secondary'
                        disabled={!props.user.canEditBusinesses}
                        onClick={() => props.importCarriers(business)}>
                        Import carriers
                      </BoldButton>
                      <BoldButton className='dropdown-item btn-ghost-secondary'
                        disabled={!props.user.canEditBusinesses}
                        onClick={() => props.manageSites(business)}>
                        Manage sites
                      </BoldButton>
                      <BoldButton className='dropdown-item btn-ghost-secondary'
                        disabled={!props.user.canEditBusinesses}
                        onClick={() => props.createSite(business)}>
                        Create site
                      </BoldButton>
                      {business.disabled
                        ? <BoldButton className='dropdown-item btn-ghost-secondary'
                          disabled={!props.user.canEditBusinesses}
                          onClick={() => props.enableSites(business)}>
                          Enable sites
                        </BoldButton>
                        : <BoldButton className='dropdown-item btn-ghost-secondary'
                          disabled={!props.user.canEditBusinesses}
                          onClick={() => props.disableSites(business)}>
                          Disable sites
                        </BoldButton>}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-icon btn-ghost-primary btn-link"
                    data-testid={`toggle-${business.id}`}
                    onClick={() => expanded.toggle(business.id)}>
                    <i className={expanded.isEnabled(business.id) ? 'bi-caret-up-square' : 'bi-caret-down-square'} />
                  </button>
                </div>
              </td>
            </tr>
            <Collapse isOpen={expanded.isEnabled(business.id)} tag="tr">
              <td colSpan={5} className="ps-7 pe-10 py-0">
                <SitesPanel sites={business.sites} onEdit={props.editSite} />
              </td>
            </Collapse>
          </React.Fragment>
        )}
      </tbody>
    </Table>
  </>);
}

const SitesPanel = ({ sites, onEdit }: { sites: SiteDto[], onEdit: (s: SiteDto) => void }) => {
  if (sites.length === 0)
    return <p className="text-muted fst-italic m-3">No sites yet for this business.</p>;

  const sorted = [...sites].sort((a, b) => a.customerName.localeCompare(b.customerName));
  return (
    <Table borderless size="sm" className="align-middle mb-3">
      <thead>
        <tr className="border-bottom">
          <th className="col-4">Kahler Site Name</th>
          <th className="col-4">Customer Site Name</th>
          <th className="col-1">Site ID</th>
          <th className="col-1" />
        </tr>
      </thead>
      <tbody>
        {sorted.map(site => (
          <tr key={site.id} className="my-0 py-0">
            <td className="py-0">{site.internalName}</td>
            <td className="py-0">{site.customerName}</td>
            <td className="py-0">{site.internalSiteId}</td>
            <td className="text-end py-0">
              <button
                type="button"
                className="btn btn-icon btn-ghost-secondary btn-link btn-sm p-0"
                onClick={() => onEdit(site)}>
                <i className="bi-pencil" />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export { BusinessList };
